import React, { memo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Close from '@mui/icons-material/Close';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import UploadFile from '@mui/icons-material/UploadFile';
import { monoSx } from '../dashboard/styles';
import FileGlyph from './FileGlyph';
import { SpecInspection } from './swaggerFileModel';

interface SpecDropZoneProps {
  file: File | null;
  /** null while the selected file is still being read. */
  inspection: SpecInspection | null;
  onSelect: (file: File) => void;
  onClear: () => void;
}

/** Drag-and-drop / browse target that turns into a validated file summary once a file is picked. */
const SpecDropZone: React.FC<SpecDropZoneProps> = ({ file, inspection, onSelect, onClear }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  if (file) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, pl: 1.75, pr: 1.5, borderRadius: 3, border: 1, borderColor: 'divider' }}>
        <FileGlyph size={40} />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography noWrap title={file.name} sx={{ ...monoSx, fontSize: 13, fontWeight: 500, color: 'text.primary' }}>
            {file.name}
          </Typography>
          {inspection === null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12, color: 'text.secondary' }}>
              <CircularProgress size={12} color="inherit" /> Checking file…
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12, color: inspection.valid ? 'primary.dark' : 'error.main' }}>
              {inspection.valid ? <CheckCircle sx={{ fontSize: 14 }} /> : <ErrorOutline sx={{ fontSize: 14 }} />}
              {inspection.valid ? inspection.summary : inspection.error}
            </Box>
          )}
        </Box>
        <IconButton aria-label="Remove file" onClick={onClear} sx={{ color: 'text.disabled' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) onSelect(dropped);
  };

  return (
    <ButtonBase
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      sx={{
        minHeight: 160,
        p: 3,
        borderRadius: 4,
        border: '2px dashed',
        borderColor: dragging ? 'primary.main' : 'divider',
        bgcolor: (theme) => (dragging ? alpha(theme.palette.primary.main, 0.05) : theme.palette.action.hover),
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        '&:hover': { borderColor: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) },
      }}
    >
      <Box sx={{ width: 48, height: 48, borderRadius: 6, bgcolor: 'brand.container', color: 'brand.onContainer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <UploadFile />
      </Box>
      <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
        Drop a file here or{' '}
        <Box component="span" sx={{ color: 'primary.main' }}>
          browse
        </Box>
      </Typography>
      <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>OpenAPI 3.x or Swagger 2.0 · .json</Typography>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept=".json,application/json"
        data-testid="swagger-file-input"
        onChange={(e) => {
          const picked = e.target.files?.[0];
          e.target.value = '';
          if (picked) onSelect(picked);
        }}
      />
    </ButtonBase>
  );
};

export default memo(SpecDropZone);
